import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, Clock, Send, Loader2, CheckCircle2, MessageCircle } from "lucide-react";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { submitEnquiry, formatApiError } from "@/lib/api";
import { STORE, CATEGORIES } from "@/lib/catalog";

export default function Contact() {
  const [form, setForm] = useState({ name: "", mobile: "", email: "", category: "", message: "" });
  const [state, setState] = useState({ status: "idle", error: "" });

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setState({ status: "loading", error: "" });
    try {
      await submitEnquiry({
        name: form.name,
        mobile: form.mobile,
        email: form.email || undefined,
        category: form.category || undefined,
        message: form.message || undefined,
      });
      setState({ status: "success", error: "" });
    } catch (err) {
      setState({ status: "error", error: formatApiError(err) });
    }
  };

  const inputCls =
    "w-full rounded-xl border border-line bg-white px-4 py-3 text-sm outline-none focus:border-forest focus:ring-2 focus:ring-forest/15 transition-shadow";

  return (
    <main className="bg-bone min-h-screen" data-testid="contact-page">
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-24">
        <Reveal>
          <Eyebrow>Contact Us</Eyebrow>
          <h1 className="mt-4 font-serif font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-ink">
            Boliye, <span className="italic text-forest">kya chahiye?</span>
          </h1>
          <p className="mt-5 max-w-xl text-ink-soft">
            Call, WhatsApp, or drop an enquiry below — we reply fast, and deliver even faster.
          </p>
        </Reveal>

        <div className="mt-14 grid lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-3">
            {state.status === "success" ? (
              <div className="rounded-3xl border border-line bg-white p-10 text-center" data-testid="contact-success-panel">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", damping: 12 }}>
                  <CheckCircle2 className="w-16 h-16 text-forest mx-auto" />
                </motion.div>
                <h2 className="mt-5 font-serif font-bold text-2xl text-ink">Enquiry received!</h2>
                <p className="mt-3 text-sm text-ink-soft leading-relaxed max-w-md mx-auto">
                  Dhanyavaad, {form.name.split(" ")[0]}! Your message has reached the store and
                  a copy is on its way to our inbox. We will call you on {form.mobile} shortly.
                </p>
                <button
                  onClick={() => {
                    setForm({ name: "", mobile: "", email: "", category: "", message: "" });
                    setState({ status: "idle", error: "" });
                  }}
                  className="mt-8 rounded-full bg-forest text-bone px-7 py-3 text-sm font-semibold hover:bg-forest-deep transition-colors"
                  data-testid="contact-send-another-button"
                >
                  Send Another Enquiry
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="rounded-3xl border border-line bg-white p-8 sm:p-10 space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.15em] text-ink-soft mb-2">Full Name *</label>
                    <input required value={form.name} onChange={set("name")} placeholder="Ramesh Gupta" className={inputCls} data-testid="contact-name-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.15em] text-ink-soft mb-2">Mobile (WhatsApp) *</label>
                    <input required value={form.mobile} onChange={set("mobile")} placeholder="98765 43210" pattern="[0-9+ -]{10,15}" className={inputCls} data-testid="contact-mobile-input" />
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.15em] text-ink-soft mb-2">Email (optional)</label>
                    <input type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} data-testid="contact-email-input" />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-[0.15em] text-ink-soft mb-2">Category</label>
                    <select value={form.category} onChange={set("category")} className={inputCls} data-testid="contact-category-select">
                      <option value="">General enquiry</option>
                      {CATEGORIES.filter((c) => c.key !== "all").map((c) => (
                        <option key={c.key} value={c.label}>{c.label}</option>
                      ))}
                      <option value="Monthly ration order">Monthly ration order</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-[0.15em] text-ink-soft mb-2">Your Order / Message *</label>
                  <textarea required rows={4} value={form.message} onChange={set("message")} placeholder="e.g. 5kg atta, 1L mustard oil, 2 Parle-G — deliver after 6 pm" className={`${inputCls} resize-none`} data-testid="contact-message-input" />
                </div>
                {state.status === "error" && (
                  <p className="text-sm text-red-600" data-testid="contact-error-message">{state.error}</p>
                )}
                <button
                  type="submit"
                  disabled={state.status === "loading"}
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-full bg-forest text-bone px-8 py-3.5 text-sm font-bold hover:bg-forest-deep transition-colors disabled:opacity-60"
                  data-testid="contact-submit-button"
                >
                  {state.status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  Send Enquiry
                </button>
              </form>
            )}
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-2 space-y-5">
            <div className="rounded-3xl bg-forest-dark text-bone p-8">
              <h3 className="font-serif font-bold text-xl">Reach the store directly</h3>
              <ul className="mt-6 space-y-5 text-sm">
                <li>
                  <p className="text-xs uppercase tracking-[0.2em] text-saffron mb-1">Owner</p>
                  <p className="font-semibold">{STORE.owner}</p>
                </li>
                <li>
                  <p className="text-xs uppercase tracking-[0.2em] text-saffron mb-1">Call / WhatsApp</p>
                  <a href={`tel:${STORE.phoneIntl.replace(/\s/g, "")}`} className="flex items-center gap-2 hover:text-saffron transition-colors font-semibold" data-testid="contact-phone-link">
                    <Phone className="w-4 h-4" /> {STORE.phoneIntl}
                  </a>
                </li>
                <li>
                  <p className="text-xs uppercase tracking-[0.2em] text-saffron mb-1">Email</p>
                  <a href={`mailto:${STORE.email}`} className="flex items-center gap-2 hover:text-saffron transition-colors break-all" data-testid="contact-email-link">
                    <Mail className="w-4 h-4 shrink-0" /> {STORE.email}
                  </a>
                </li>
                <li>
                  <p className="text-xs uppercase tracking-[0.2em] text-saffron mb-1">Address</p>
                  <p className="flex items-start gap-2 text-bone/85">
                    <MapPin className="w-4 h-4 mt-0.5 shrink-0" /> {STORE.address}
                  </p>
                </li>
                <li>
                  <p className="text-xs uppercase tracking-[0.2em] text-saffron mb-1">Hours</p>
                  <p className="flex items-center gap-2 text-bone/85">
                    <Clock className="w-4 h-4 shrink-0" /> {STORE.hours}
                  </p>
                </li>
              </ul>
              <a
                href={STORE.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 flex items-center justify-center gap-2 rounded-full bg-saffron text-forest-dark py-3 text-sm font-bold hover:bg-bone transition-colors"
                data-testid="contact-whatsapp-button"
              >
                <MessageCircle className="w-4 h-4" /> Chat on WhatsApp
              </a>
            </div>
            <div className="rounded-3xl border border-line bg-cream p-8 text-center">
              <MapPin className="w-8 h-8 text-marigold mx-auto" />
              <p className="mt-3 font-serif italic text-lg text-ink">Find us on Main Market Road</p>
              <p className="mt-1 text-xs text-ink-muted">Civil Lines, Kanpur — next to the old banyan chauraha.</p>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
